/**
 * Shared credit-gate errors for ALL generation endpoints (video, script,
 * audio, image, voice). The backend reports insufficient pixel credits as
 * 402 (usually with a JSON body carrying the required/available balances)
 * and a missing entitlement as 403 with a NO_ACTIVE_ENTITLEMENT code.
 * Every generation service throws these typed errors via
 * `throwForCreditGate` so callers can render top-up vs subscribe upsells
 * instead of generic failure toasts.
 *
 * Dependency-free (no imports) so it loads in the node:test sandbox with
 * no stubs.
 */

export class InsufficientPixlesError extends Error {
  required: number;
  available: number;
  pixlesCharged: number;

  constructor(
    message: string,
    fields?: { required?: number; available?: number; pixlesCharged?: number }
  ) {
    super(message);
    this.name = 'InsufficientPixlesError';
    this.required = fields?.required ?? 0;
    this.available = fields?.available ?? 0;
    this.pixlesCharged = fields?.pixlesCharged ?? 0;
  }
}

export class NoActiveEntitlementError extends Error {
  code: string;

  constructor(message: string, code = 'NO_ACTIVE_ENTITLEMENT') {
    super(message);
    this.name = 'NoActiveEntitlementError';
    this.code = code;
  }
}

export interface CreditGateLabels {
  /** Fallback message for 402 (backend message wins when present). */
  insufficient: string;
  /** Fallback message for 403 (backend message wins when present). */
  noEntitlement: string;
  /** Fallback message for any other failure status. */
  failed: string;
}

const toFiniteNumberOrUndefined = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const toNonEmptyStringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/** Normalize a response body (raw text or an already-parsed value). */
const asParsedBody = (body: unknown): any => {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      // Non-JSON body (e.g. a plain-text provider error) — keep the text.
      return trimmed;
    }
  }
  return body ?? null;
};

const readCode = (parsed: any, fallback: string): string => {
  const code =
    parsed?.code ?? parsed?.error?.code ?? parsed?.data?.code ?? fallback;
  return typeof code === 'string' && code !== '' ? code : fallback;
};

const readMessage = (
  parsed: any,
  bodyText: string,
  fallback: string
): string => {
  const backendMessage =
    toNonEmptyStringOrUndefined(parsed?.error) ??
    toNonEmptyStringOrUndefined(parsed?.message);
  if (backendMessage) return backendMessage;
  if (typeof parsed === 'string' && parsed !== '') return parsed;
  if (bodyText !== '') return bodyText;
  return fallback;
};

/**
 * Throw the appropriate error for a failed generation response. Always
 * throws (returns `never`); the 402/403 branches throw the typed errors
 * above, anything else a plain Error carrying the backend message.
 */
export const throwForCreditGate = (
  status: number,
  body: unknown,
  labels: CreditGateLabels
): never => {
  const bodyText = typeof body === 'string' ? body : '';
  const parsed = asParsedBody(body);

  if (status === 402) {
    if (parsed !== null && typeof parsed === 'object') {
      throw new InsufficientPixlesError(
        readMessage(parsed, bodyText, labels.insufficient),
        {
          required: toFiniteNumberOrUndefined(parsed?.required),
          available: toFiniteNumberOrUndefined(parsed?.available),
          pixlesCharged:
            toFiniteNumberOrUndefined(parsed?.pixlesCharged) ??
            toFiniteNumberOrUndefined(parsed?.pixelsCharged),
        }
      );
    }
    throw new InsufficientPixlesError(
      readMessage(parsed, bodyText, labels.insufficient)
    );
  }

  if (status === 403) {
    throw new NoActiveEntitlementError(
      readMessage(parsed, bodyText, labels.noEntitlement),
      typeof parsed === 'object' && parsed !== null
        ? readCode(parsed, 'NO_ACTIVE_ENTITLEMENT')
        : 'NO_ACTIVE_ENTITLEMENT'
    );
  }

  throw new Error(readMessage(parsed, bodyText, labels.failed));
};
